import React, { useMemo, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Trash2, Eye, EyeOff, Plus, X, Ratio, Square, ChevronLeft, Armchair, Link2, Pencil } from 'lucide-react';
import { AdvancedColorPicker } from './AdvancedColorPicker';

const PropertyInput = React.memo(({ label, onFinalChange, initialValue, ...props }) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleBlur = () => {
        onFinalChange(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onFinalChange(value);
            e.target.blur();
        } else if (e.key === 'Escape') {
            setValue(initialValue);
            e.target.blur();
        }
    };

    return (
        <div>
            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">{label}</label>
            <input
                {...props}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
            />
        </div>
    );
});


const MaterialDisplay = ({ materialId, materials }) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) {
        return <div className="text-sm text-gray-500 dark:text-gray-400 p-2 rounded-md bg-gray-100 dark:bg-gray-700/50">None</div>;
    }
    return (
        <div className="flex items-center gap-2 p-2 rounded-md bg-gray-100 dark:bg-gray-700/50">
            <div className="w-5 h-5 rounded-full border dark:border-gray-500 flex-shrink-0 bg-cover bg-center" style={{ backgroundColor: material.color, backgroundImage: `url(${material.textureUrl})`}}></div>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{material.name}</span>
        </div>
    );
};

const MaterialSection = ({ label, materialIds, targetKey, openLibrary, onPropertyChange, canManage, materials }) => {
    const handleAdd = () => {
        const type = targetKey === 'edgeWallMaterials' ? 'edge' : 'entity';
        openLibrary(targetKey, { index: -1, type });
    };
    const handleChange = (index) => {
        const type = targetKey === 'edgeWallMaterials' ? 'edge' : 'entity';
        openLibrary(targetKey, { index, type });
    };
    const handleRemove = (indexToRemove) => {
        const newMaterialIds = (materialIds || []).filter((_, i) => i !== indexToRemove);
         if (targetKey === 'edgeWallMaterials') {
            onPropertyChange('wallMaterials', newMaterialIds, 'edge');
        } else {
            onPropertyChange(targetKey, newMaterialIds, 'entity');
        }
    };

    return (
        <div>
            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">{label}</label>
            <div className="space-y-2">
                {(materialIds || []).map((materialId, index) => (
                     <div key={index} className="flex items-center gap-2">
                        <div className="flex-grow">
                            <MaterialDisplay materialId={materialId} materials={materials} />
                        </div>
                         {canManage && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    onClick={() => handleChange(index)}
                                    className="px-3 py-2 text-xs font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/80 transition-colors"
                                >
                                    Change
                                </button>
                                <button
                                    onClick={() => handleRemove(index)}
                                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                                    title="Remove Material"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                         )}
                    </div>
                ))}
                 {canManage && (
                    <button
                        onClick={handleAdd}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700/50 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add Material
                    </button>
                )}
            </div>
        </div>
    );
};

const FloorSlotsSection = ({ slots = [], onPropertyChange, openLibrary, canManage, materials }) => {

    const handleAddSlot = () => {
        const newSlot = {
            key: uuidv4(),
            name: `Floor ${slots.length + 1}`,
            materialIds: [],
        };
        onPropertyChange('floorSlots', [...slots, newSlot]);
    };

    const handleDeleteSlot = (slotKey) => {
        onPropertyChange('floorSlots', slots.filter(s => s.key !== slotKey));
    };

    const handleSlotNameChange = (slotKey, newName) => {
        onPropertyChange('floorSlots', slots.map(s => (s.key === slotKey ? { ...s, name: newName } : s)));
    };

    const handleOpenLibraryForSlot = (slotKey, materialIndex = -1) => {
        openLibrary('floorSlots', { slotKey, materialIndex });
    };

    const handleRemoveMaterial = (slotKey, materialIndex) => {
        const newSlots = slots.map(s => {
            if (s.key === slotKey) {
                const newMaterialIds = s.materialIds.filter((_, i) => i !== materialIndex);
                return { ...s, materialIds: newMaterialIds };
            }
            return s;
        });
        onPropertyChange('floorSlots', newSlots);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Floor Slots</label>
                {canManage && (
                    <button onClick={handleAddSlot} className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <Plus className="h-5 w-5" />
                    </button>
                )}
            </div>
            <div className="space-y-4">
                {slots.map((slot) => (
                    <div key={slot.key} className="p-3 border dark:border-gray-700 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={slot.name}
                                onChange={(e) => handleSlotNameChange(slot.key, e.target.value)}
                                disabled={!canManage}
                                className="w-full text-sm font-semibold bg-transparent outline-none text-gray-800 dark:text-gray-200"
                            />
                            {canManage && (
                                <button onClick={() => handleDeleteSlot(slot.key)} className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100/50 dark:hover:bg-red-900/20">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {(slot.materialIds || []).map((materialId, materialIndex) => (
                                <div key={materialIndex} className="flex items-center gap-2">
                                    <div className="flex-grow">
                                        <MaterialDisplay materialId={materialId} materials={materials} />
                                    </div>
                                    {canManage && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button onClick={() => handleOpenLibraryForSlot(slot.key, materialIndex)} className="px-3 py-2 text-xs font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/80">
                                                Change
                                            </button>
                                            <button onClick={() => handleRemoveMaterial(slot.key, materialIndex)} className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-100 dark:hover:bg-red-900/20" title="Remove Material">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {canManage && (
                                <button onClick={() => handleOpenLibraryForSlot(slot.key, -1)} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700/50">
                                    <Plus className="h-4 w-4" />
                                    Add Material
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const WallSlotsSection = ({ slots = [], onPropertyChange, openLibrary, canManage, materials }) => {

    const handleAddSlot = () => {
        const newSlot = {
            key: uuidv4(),
            name: `Wall ${slots.length + 1}`,
            materialIds: [],
        };
        onPropertyChange('wallSlots', [...slots, newSlot]);
    };

    const handleDeleteSlot = (slotKey) => {
        onPropertyChange('wallSlots', slots.filter(s => s.key !== slotKey));
    };

    const handleSlotNameChange = (slotKey, newName) => {
        onPropertyChange('wallSlots', slots.map(s => (s.key === slotKey ? { ...s, name: newName } : s)));
    };

    const handleOpenLibraryForSlot = (slotKey, materialIndex = -1) => {
        openLibrary('wallSlots', { slotKey, materialIndex });
    };

    const handleRemoveMaterial = (slotKey, materialIndex) => {
        const newSlots = slots.map(s => {
            if (s.key === slotKey) {
                const newMaterialIds = s.materialIds.filter((_, i) => i !== materialIndex);
                return { ...s, materialIds: newMaterialIds };
            }
            return s;
        });
        onPropertyChange('wallSlots', newSlots);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Wall Slots</label>
                {canManage && (
                    <button onClick={handleAddSlot} className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <Plus className="h-5 w-5" />
                    </button>
                )}
            </div>
            <div className="space-y-4">
                {slots.map((slot) => (
                    <div key={slot.key} className="p-3 border dark:border-gray-700 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={slot.name}
                                onChange={(e) => handleSlotNameChange(slot.key, e.target.value)}
                                disabled={!canManage}
                                className="w-full text-sm font-semibold bg-transparent outline-none text-gray-800 dark:text-gray-200"
                            />
                            {canManage && (
                                <button onClick={() => handleDeleteSlot(slot.key)} className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100/50 dark:hover:bg-red-900/20">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {(slot.materialIds || []).map((materialId, materialIndex) => (
                                <div key={materialIndex} className="flex items-center gap-2">
                                    <div className="flex-grow">
                                        <MaterialDisplay materialId={materialId} materials={materials} />
                                    </div>
                                    {canManage && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button onClick={() => handleOpenLibraryForSlot(slot.key, materialIndex)} className="px-3 py-2 text-xs font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/80">
                                                Change
                                            </button>
                                            <button onClick={() => handleRemoveMaterial(slot.key, materialIndex)} className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-100 dark:hover:bg-red-900/20" title="Remove Material">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {canManage && (
                                <button onClick={() => handleOpenLibraryForSlot(slot.key, -1)} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700/50">
                                    <Plus className="h-4 w-4" />
                                    Add Material
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const FurnitureSection = ({ furnitureIds, onPropertyChange, canManage, furniture, onAddFurnitureClick, activeFurnitureIndex }) => {

    const handleRemove = (indexToRemove) => {
        const newFurnitureIds = (furnitureIds || []).filter((_, i) => i !== indexToRemove);
        onPropertyChange('furnitureIds', newFurnitureIds, 'entity');
        
        const newActiveIndex = (activeFurnitureIndex || 0);
        if (newActiveIndex >= newFurnitureIds.length && newFurnitureIds.length > 0) {
            onPropertyChange('activeFurnitureIndex', newFurnitureIds.length - 1, 'entity');
        } else if (newFurnitureIds.length === 0) {
            onPropertyChange('activeFurnitureIndex', 0, 'entity');
        }
    };

    return (
        <div>
            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">Furniture Options</label>
            <div className="space-y-2">
                {(furnitureIds || []).map((furnitureId, index) => {
                    const item = furniture?.find(f => f.id === furnitureId);
                    return (
                        <div key={index} className="flex items-center gap-2">
                            <div className="flex-grow flex items-center gap-2 p-2 rounded-md bg-gray-100 dark:bg-gray-700/50">
                                {item?.imageUrl && <img src={item.imageUrl} alt={item.name} className="w-8 h-8 object-cover rounded-sm" />}
                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{item?.name || 'Unknown Furniture'}</span>
                            </div>
                            {canManage && furnitureIds.length > 1 && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                        onClick={() => handleRemove(index)}
                                        className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                                        title="Remove Option"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                })}
                {canManage && (
                    <button
                        onClick={onAddFurnitureClick}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700/50 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add Option
                    </button>
                )}
            </div>
        </div>
    );
};


export const PropertiesPanel = ({ selectedEntity, selectedEntityIds, onPropertyChange, onDelete, canManage, isDark, onRestoreAspectRatio, materials, furniture, decorativeImages, openLibrary, onSelectEdge, selectedEdgeIndices, onClearEdgeSelection, onDeleteCustomEdge, entities, onAddFurnitureClick, onAddFurnitureOption, onSelectEntity, onEditItem }) => {
    
    const areEdgesSelected = useMemo(() => selectedEdgeIndices && selectedEdgeIndices.length > 0, [selectedEdgeIndices]);
    const linkedFurniture = useMemo(() => {
        if (!selectedEntity || !entities) return [];
        return entities.filter(e => e.type === 'image' && e.purpose === 'furniture' && e.linkedAreaId === selectedEntity.id)
    }, [entities, selectedEntity]);

    const [linkedItem, setLinkedItem] = useState({ item: null, type: null });

    useEffect(() => {
        if (!selectedEntity || selectedEntity.type !== 'image') {
            setLinkedItem({ item: null, type: null });
            return;
        }

        let foundItem = null;
        let foundType = null;

        if (selectedEntity.purpose === 'material' && selectedEntity.materialIds?.length > 0 && materials) {
            const activeMaterial = materials.find(m => m.id === selectedEntity.materialIds[selectedEntity.activeMaterialIndex || 0]);
            if (activeMaterial) {
                foundItem = activeMaterial;
                foundType = 'material';
            }
        } else if (selectedEntity.purpose === 'furniture' && selectedEntity.furnitureIds?.length > 0 && furniture) {
            const activeFurniture = furniture.find(f => f.id === selectedEntity.furnitureIds[selectedEntity.activeFurnitureIndex || 0]);
            if (activeFurniture) {
                foundItem = activeFurniture;
                foundType = 'furniture';
            }
        } else if (selectedEntity.purpose === 'decorative' && selectedEntity.decorativeImageId && decorativeImages) {
            const activeImage = decorativeImages.find(i => i.id === selectedEntity.decorativeImageId);
            if (activeImage) {
                foundItem = activeImage;
                foundType = 'decorative';
            }
        }
        
        setLinkedItem({ item: foundItem, type: foundType });

    }, [selectedEntity, materials, furniture, decorativeImages]);

    if (selectedEntityIds && selectedEntityIds.length > 1) {
        return (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 border-2 border-dashed dark:border-gray-600 rounded-lg p-8 h-full flex flex-col justify-center items-center">
                <Square className="h-8 w-8 mx-auto text-gray-400" />
                <p className="mt-2 font-bold">{selectedEntityIds.length} items selected</p>
                <p className="mt-1">Please select a single item to edit its properties.</p>
            </div>
        );
    }

    if (areEdgesSelected) {
        if (!selectedEntity || !selectedEntity.edges) return null;
        const allSelectedEdges = selectedEdgeIndices.map(i => selectedEntity.edges[i]).filter(Boolean);
        if (allSelectedEdges.length === 0) return null;

        const firstEdge = allSelectedEdges[0] || {};
        
        const { 
            name: firstEdgeName = '', 
            textSize: firstEdgeTextSize = 12, 
            textColor: firstEdgeTextColor = '#047857' 
        } = firstEdge;

        const allNamesMatch = allSelectedEdges.every(edge => (edge?.name || '') === firstEdgeName);
        const displayName = allNamesMatch ? firstEdgeName : '';

        const handleNameChange = (e) => {
            onPropertyChange('name', e.target.value, 'edge');
        }

        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <button 
                        onClick={onClearEdgeSelection} 
                        className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                        title="Back to Area Properties"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">
                        Edge Properties ({selectedEdgeIndices.length} selected)
                    </h2>
                    {canManage && (
                        <button onClick={onDeleteCustomEdge} className="ml-auto p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors" title="Clear Edge Customizations">
                            <Trash2 className="h-5 w-5" />
                        </button>
                    )}
                </div>
                <div className="space-y-6">
                     <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Edge Name</label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={handleNameChange}
                            placeholder={allNamesMatch ? 'Custom Edge Name' : 'Multiple Values'}
                            disabled={!canManage}
                            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                        />
                    </div>
                     <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Text Size</label>
                        <input
                            type="number"
                            min="1"
                            value={firstEdgeTextSize}
                            onChange={(e) => onPropertyChange('textSize', parseInt(e.target.value, 10) || 12, 'edge')}
                            disabled={!canManage}
                            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                        />
                    </div>
                     <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Text Color</label>
                        <AdvancedColorPicker
                            value={firstEdgeTextColor}
                            onChange={(color) => onPropertyChange('textColor', color, 'edge')}
                            disabled={!canManage}
                        />
                    </div>
                    <MaterialSection
                        label="Wall Materials"
                        materialIds={firstEdge.wallMaterials || []}
                        targetKey="edgeWallMaterials"
                        openLibrary={(key, opts) => openLibrary(key, {...opts, type: 'edge'})}
                        onPropertyChange={onPropertyChange}
                        canManage={canManage}
                        materials={materials}
                    />
                </div>
            </div>
        );
    }
    
    if (!selectedEntity) {
        return (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 border-2 border-dashed dark:border-gray-600 rounded-lg p-8 h-full flex flex-col justify-center items-center">
                <Square className="h-8 w-8 mx-auto text-gray-400" />
                <p className="mt-2">Select an element on the canvas to see its properties.</p>
            </div>
        );
    }
    
    // Area Properties
    if (['polygon', 'rect', 'ellipse'].includes(selectedEntity.type)) {
        const isNameVisible = selectedEntity.nameVisible ?? true;
        const defaultTextColor = isDark ? '#FFFFFF' : '#000000';
        const customEdges = (selectedEntity.edges || []).map((edge, index) => ({...edge, index})).filter(edge => (edge.name || (edge.wallMaterials && edge.wallMaterials.length > 0)));

        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">Area Properties</h2>
                    {canManage && (
                        <button onClick={onDelete} className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                            <Trash2 className="h-5 w-5" />
                        </button>
                    )}
                </div>
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Name</label>
                        <input
                            type="text"
                            value={selectedEntity.name}
                            onChange={(e) => onPropertyChange('name', e.target.value)}
                            disabled={!canManage}
                            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                        />
                    </div>

                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Label Settings</h3>
                        <label className="flex items-center justify-between cursor-pointer">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Show Name Label</span>
                            <input
                                type="checkbox"
                                checked={isNameVisible}
                                onChange={(e) => onPropertyChange('nameVisible', e.target.checked)}
                                disabled={!canManage}
                                className="sr-only peer"
                            />
                            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
                        </label>
                         <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Label Text Size</label>
                            <input
                                type="number"
                                min="1"
                                value={selectedEntity.nameTextSize || 14}
                                onChange={(e) => onPropertyChange('nameTextSize', parseInt(e.target.value, 10) || 14)}
                                disabled={!canManage || !isNameVisible}
                                className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Label Text Color</label>
                            <AdvancedColorPicker
                                value={selectedEntity.nameTextColor || defaultTextColor}
                                onChange={(color) => onPropertyChange('nameTextColor', color)}
                                disabled={!canManage || !isNameVisible}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Label X Offset</label>
                                <input
                                    type="number"
                                    value={selectedEntity.nameOffsetX || 0}
                                    onChange={(e) => onPropertyChange('nameOffsetX', parseInt(e.target.value, 10) || 0)}
                                    disabled={!canManage || !isNameVisible}
                                    className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Label Y Offset</label>
                                <input
                                    type="number"
                                    value={selectedEntity.nameOffsetY || 0}
                                    onChange={(e) => onPropertyChange('nameOffsetY', parseInt(e.target.value, 10) || 0)}
                                    disabled={!canManage || !isNameVisible}
                                    className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Area Fill Style</h3>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Fill Color</label>
                            <AdvancedColorPicker
                                value={selectedEntity.fillColor || '#3b82f6'}
                                onChange={(color) => onPropertyChange('fillColor', color, 'entity')}
                                disabled={!canManage}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Fill Opacity</label>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="range" 
                                    min="0" max="1" step="0.05" 
                                    value={selectedEntity.fillOpacity ?? 0.05} 
                                    onChange={(e) => onPropertyChange('fillOpacity', parseFloat(e.target.value), 'entity')}
                                    disabled={!canManage} 
                                    className="w-full mt-1 accent-blue-600"
                                />
                                <span className="text-sm font-mono text-gray-500 dark:text-gray-400 w-12 text-right">{Math.round((selectedEntity.fillOpacity ?? 0.05) * 100)}%</span>
                            </div>
                        </div>
                    </div>

                    <FloorSlotsSection
                        slots={selectedEntity.floorSlots}
                        onPropertyChange={(prop, value) => onPropertyChange(prop, value, 'entity')}
                        openLibrary={openLibrary}
                        canManage={canManage}
                        materials={materials}
                    />
                    <WallSlotsSection
                        slots={selectedEntity.wallSlots}
                        onPropertyChange={(prop, value) => onPropertyChange(prop, value, 'entity')}
                        openLibrary={openLibrary}
                        canManage={canManage}
                        materials={materials}
                    />
                    <MaterialSection label="Ceiling Materials" materialIds={selectedEntity.ceilingMaterials} targetKey="ceilingMaterials" openLibrary={(key, opts) => openLibrary(key, {...opts, type: 'entity'})} onPropertyChange={onPropertyChange} canManage={canManage} materials={materials} />

                    {customEdges.length > 0 && (
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">Custom Edges</label>
                            <div className="space-y-2 border-t dark:border-gray-600 pt-4">
                                {customEdges.map(edge => (
                                    <div key={edge.index} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer" onClick={() => onSelectEdge(edge.index)}>
                                        <p className="font-semibold text-xs text-gray-700 dark:text-gray-300">{edge.name || `Edge ${edge.index + 1}`}</p>
                                        {edge.wallMaterials && edge.wallMaterials.length > 0 && (
                                            <div className="mt-1 space-y-1">
                                                {edge.wallMaterials.map(materialId => (
                                                    <MaterialDisplay key={materialId} materialId={materialId} materials={materials} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                     <div className="border-t dark:border-gray-600 pt-4 space-y-2">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Furniture</h3>
                            {canManage && (
                                <button
                                    onClick={onAddFurnitureClick}
                                    className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Add Furniture"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        {linkedFurniture.length > 0 ? (
                            <div className="space-y-2">
                                {linkedFurniture.map(item => (
                                    <div key={item.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-100 dark:bg-gray-700/50">
                                        <div className="flex items-center gap-2 truncate">
                                            <Armchair className="h-4 w-4 text-gray-500 flex-shrink-0"/>
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{item.name}</p>
                                        </div>
                                        {canManage && (
                                            <button
                                                onClick={() => onDelete(item)}
                                                className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100/50 dark:hover:bg-red-900/20"
                                                title="Remove Furniture Link"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-center text-gray-400 dark:text-gray-500 py-2">No furniture linked to this area.</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    // Image / Box Properties
    if (selectedEntity.type === 'image') {
        const isOverlayEnabled = selectedEntity.colorOverlayEnabled ?? false;

        const ImagePropertyFields = () => {
            const handleDisplayInChange = (mode, isChecked) => {
                if (!canManage) return;
                const current = selectedEntity.displayIn ?? ['material', 'furniture'];
                let newDisplayIn;
                if (isChecked) {
                    newDisplayIn = [...new Set([...current, mode])];
                } else {
                    newDisplayIn = current.filter(m => m !== mode);
                }
                onPropertyChange('displayIn', newDisplayIn);
            };

            let derivedName = selectedEntity.name || '';
            let derivedDescription = selectedEntity.description || '';
            let isNameDisabled = !canManage;

            if (selectedEntity.purpose === 'material' && selectedEntity.materialIds?.length > 0 && materials) {
                const activeMaterial = materials.find(m => m.id === selectedEntity.materialIds[selectedEntity.activeMaterialIndex || 0]);
                if (activeMaterial) {
                    derivedName = activeMaterial.name;
                    derivedDescription = activeMaterial.description;
                    isNameDisabled = true;
                }
            } else if (selectedEntity.purpose === 'furniture' && selectedEntity.furnitureIds?.length > 0 && furniture) {
                const activeFurniture = furniture.find(f => f.id === selectedEntity.furnitureIds[selectedEntity.activeFurnitureIndex || 0]);
                if (activeFurniture) {
                    derivedName = activeFurniture.name;
                    derivedDescription = activeFurniture.description;
                    isNameDisabled = true;
                }
            } else if (selectedEntity.purpose === 'decorative' && selectedEntity.decorativeImageId && decorativeImages) {
                const activeImage = decorativeImages.find(i => i.id === selectedEntity.decorativeImageId);
                if (activeImage) {
                    derivedName = activeImage.name;
                    derivedDescription = activeImage.description;
                    isNameDisabled = true;
                }
            }


            return (
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Name</label>
                        <input
                            type="text"
                            value={derivedName}
                            onChange={(e) => onPropertyChange('name', e.target.value)}
                            disabled={isNameDisabled}
                            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                        />
                    </div>
                    {selectedEntity.purpose === 'decorative' && (
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Description</label>
                            <textarea value={derivedDescription} onChange={(e) => onPropertyChange('description', e.target.value)} disabled={isNameDisabled} rows="3" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50" />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <PropertyInput label="Width" type="number" initialValue={Math.round(selectedEntity.width)} onFinalChange={(val) => onPropertyChange('width', parseFloat(val) || 0)} disabled={!canManage} />
                        <PropertyInput label="Height" type="number" initialValue={Math.round(selectedEntity.height)} onFinalChange={(val) => onPropertyChange('height', parseFloat(val) || 0)} disabled={!canManage} />
                    </div>
                    {canManage && (
                        <button
                            onClick={onRestoreAspectRatio}
                            disabled={!selectedEntity.originalAspectRatio}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Ratio className="h-4 w-4" />
                            Restore Aspect Ratio
                        </button>
                    )}

                    {selectedEntity.purpose === 'decorative' && selectedEntity.linkedAreaId && (
                        <div className="border-t dark:border-gray-600 pt-4 space-y-2">
                            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">View Mode Display</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">When the area is focused, show this image when viewing:</p>
                            <div className="space-y-2 pt-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedEntity.displayIn?.includes('material') ?? true}
                                        onChange={(e) => handleDisplayInChange('material', e.target.checked)}
                                        disabled={!canManage}
                                        className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Materials</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedEntity.displayIn?.includes('furniture') ?? true}
                                        onChange={(e) => handleDisplayInChange('furniture', e.target.checked)}
                                        disabled={!canManage}
                                        className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Furniture</span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Area Link</h3>
                        
                        {selectedEntity.purpose === 'decorative' ? (
                            <div className="flex items-center gap-2">
                                <Link2 className="h-4 w-4 text-gray-400"/>
                                <select
                                    value={selectedEntity.linkedAreaId || ''}
                                    onChange={(e) => onPropertyChange('linkedAreaId', e.target.value || null)}
                                    disabled={!canManage}
                                    className="w-full px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <option value="">Not Linked</option>
                                    {entities.filter(e => ['polygon', 'rect', 'ellipse'].includes(e.type)).map(area => (
                                        <option key={area.id} value={area.id}>{area.name}</option>
                                    ))}
                                </select>
                            </div>
                        ) : selectedEntity.linkedAreaId ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Link2 className="h-4 w-4 text-gray-400"/>
                                <span>Linked to: <span className="font-semibold">{entities.find(e => e.id === selectedEntity.linkedAreaId)?.name || 'an area'}</span></span>
                            </div>
                        ) : null}

                        {selectedEntity.linkedAreaId && (
                            <label className="flex items-center justify-between cursor-pointer pt-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">Show Link Line</span>
                                <input
                                    type="checkbox"
                                    checked={selectedEntity.isLinkVisible ?? true}
                                    onChange={(e) => onPropertyChange('isLinkVisible', e.target.checked)}
                                    disabled={!canManage}
                                    className="sr-only peer"
                                />
                                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
                            </label>
                        )}
                    </div>

                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Border & Style</h3>
                        <div className="grid grid-cols-2 gap-4">
                             <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Corner Radius</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={selectedEntity.borderRadius || 0}
                                    onChange={(e) => onPropertyChange('borderRadius', parseFloat(e.target.value) || 0)}
                                    disabled={!canManage}
                                    className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                                />
                            </div>
                             <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Border Width</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={selectedEntity.borderWidth || 0}
                                    onChange={(e) => onPropertyChange('borderWidth', parseFloat(e.target.value) || 0)}
                                    disabled={!canManage}
                                    className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Border Color</label>
                             <AdvancedColorPicker
                                value={selectedEntity.borderColor || '#000000'}
                                onChange={(color) => onPropertyChange('borderColor', color)}
                                disabled={!canManage || !(selectedEntity.borderWidth > 0)}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Opacity</label>
                        <input type="range" min="0" max="1" step="0.05" value={selectedEntity.opacity ?? 1} onChange={(e) => onPropertyChange('opacity', parseFloat(e.target.value))} disabled={!canManage} className="w-full mt-1 accent-blue-600" />
                    </div>
                    
                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Color Overlay</h3>
                        <label className="flex items-center justify-between cursor-pointer">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Enable Overlay</span>
                            <input
                                type="checkbox"
                                checked={isOverlayEnabled}
                                onChange={(e) => onPropertyChange('colorOverlayEnabled', e.target.checked)}
                                disabled={!canManage}
                                className="sr-only peer"
                            />
                            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
                        </label>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Overlay Color</label>
                            <AdvancedColorPicker
                                value={selectedEntity.colorOverlay || '#000000'}
                                onChange={(color) => onPropertyChange('colorOverlay', color)}
                                disabled={!canManage || !isOverlayEnabled}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Overlay Opacity</label>
                            <input type="range" min="0" max="1" step="0.05" value={selectedEntity.colorOverlayOpacity ?? 0.4} onChange={(e) => onPropertyChange('colorOverlayOpacity', parseFloat(e.target.value))} disabled={!canManage || !isOverlayEnabled} className="w-full mt-1 accent-blue-600 disabled:opacity-50" />
                        </div>
                    </div>
                </div>
            );
        }

        if (selectedEntity.purpose === 'furniture') {
            return (
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        {selectedEntity.linkedAreaId && onSelectEntity && (
                             <button 
                                onClick={() => onSelectEntity([selectedEntity.linkedAreaId])} 
                                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                                title="Back to Area Properties"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                        )}
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">Furniture Properties</h2>
                        <div className="ml-auto flex items-center">
                            {canManage && linkedItem.item && (
                                <button
                                    onClick={() => onEditItem(linkedItem.item, linkedItem.type)}
                                    className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                    title={`Edit ${linkedItem.type}`}
                                >
                                    <Pencil className="h-5 w-5" />
                                </button>
                            )}
                            {canManage && (
                                <button onClick={onDelete} className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                                    <Trash2 className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>
                     <div className="border-t dark:border-gray-600 pt-4">
                        <FurnitureSection
                            furnitureIds={selectedEntity.furnitureIds}
                            activeFurnitureIndex={selectedEntity.activeFurnitureIndex}
                            onPropertyChange={onPropertyChange}
                            canManage={canManage}
                            furniture={furniture}
                            onAddFurnitureClick={onAddFurnitureOption}
                        />
                    </div>
                    <ImagePropertyFields />
                </div>
            );
        }
        
        return (
             <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">{selectedEntity.purpose || 'Image'} Properties</h2>
                    <div className="flex items-center">
                        {canManage && linkedItem.item && (
                            <button
                                onClick={() => onEditItem(linkedItem.item, linkedItem.type)}
                                className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                title={`Edit ${linkedItem.type}`}
                            >
                                <Pencil className="h-5 w-5" />
                            </button>
                        )}
                        {canManage && (
                            <button onClick={onDelete} className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                                <Trash2 className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>
                <ImagePropertyFields />
            </div>
        )
    }

    return (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 p-8">
            <p>Select an element to see its properties.</p>
        </div>
    );
};
